import { type OrderListFilters } from 'app/src/model'
import { type StatusConfigType } from 'app/src/services/welfareportal'

import { DateTime } from 'luxon'
import { Button } from 'primereact/button'
import { Calendar } from 'primereact/calendar'
import { Card } from 'primereact/card'
import { MultiSelect } from 'primereact/multiselect'
import { useEffect, useState, type Dispatch, type ReactElement, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'

export function UserAccountOrderListFilterSection(props: {
  statusList: StatusConfigType[]
  selectedFilters: OrderListFilters
  defaultFilters: OrderListFilters
  updateFilters: Dispatch<SetStateAction<OrderListFilters>>
  visible: boolean
}): ReactElement {
  const { t } = useTranslation('userAccount', { keyPrefix: 'FilterSection' })
  console.log('render FilterSection status', props.selectedFilters.status)

  const [selectedStatusFilters, setSelectedStatusFilters] = useState(props.selectedFilters.status)
  const [selectedStatusToDisplay, setSelectedStatusToDisplay] = useState<StatusConfigType[] | undefined>()
  const [fromDate, setFromDate] = useState<DateTime | undefined>(undefined)
  const [toDate, setToDate] = useState<DateTime | undefined>(undefined)
  const [selectedRange, setSelectedRange] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (props.selectedFilters.status !== undefined) {
      const statusToDisplay = props.statusList.filter((e: StatusConfigType) => e.key in props.selectedFilters.status!)
      setSelectedStatusToDisplay(statusToDisplay)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const dateRanges = [
    {
      label: t('LastSevenDays'),
      range: [DateTime.now().minus({ days: 7 }).startOf('day'), DateTime.now().endOf('day')],
    },
    {
      label: t('LastMonth'),
      range: [DateTime.now().minus({ months: 1 }).startOf('day'), DateTime.now().endOf('day')],
    },
    {
      label: t('LastThreeMonths'),
      range: [DateTime.now().minus({ months: 3 }).startOf('day'), DateTime.now().endOf('day')],
    },
  ]

  const handleDateRangeChange = (range: DateTime[], label: string) => {
    // setDateRange(range)
    setSelectedRange(label)
    setFromDate(range[0])
    setToDate(range[1].endOf('day'))
  }

  const today = DateTime.now()

  return (
    <div className='grid'>
      {props.visible && (
        <Card className='mb-4' pt={{ body: { className: 'p-4' } }}>
          <div className='mb-4 grid gap-2 xl:hidden'>
            <div className='flex flex-col md:flex-row'>
              <div>
                <label htmlFor='filters' className='mr-1 block'>
                  {t('StatusFilterLabel')}
                </label>
                <MultiSelect
                  id='filters'
                  pt={{
                    header: { className: 'pt-1 pl-4' },
                    checkbox: { root: { className: 'flex vertical-middle' } },
                  }}
                  value={selectedStatusToDisplay}
                  onChange={(e) => {
                    if (e.value.length > 0) {
                      const filterArray: string[] = e.value.map((v: StatusConfigType) => v.key)

                      setSelectedStatusFilters(filterArray)
                    } else {
                      console.log('reset status filters to default: ', props.defaultFilters.status)
                      setSelectedStatusFilters(props.defaultFilters.status)
                    }
                    setSelectedStatusToDisplay(e.value)
                  }}
                  options={props.statusList}
                  optionLabel='label'
                  placeholder={t('StatusFilterDropdownPlaceholder')}
                  className='w-64'
                />
              </div>
            </div>
            <div>
              {t('RangeLabel')}
              <div>
                {dateRanges.map((dateRangeObj, index) => (
                  <Button
                    key={index}
                    rounded
                    label={dateRangeObj.label}
                    onClick={() => handleDateRangeChange(dateRangeObj.range, dateRangeObj.label)}
                    className={selectedRange === dateRangeObj.label ? 'p-button-primary' : 'p-button-outlined'}
                    style={{ margin: '5px' }}
                  />
                ))}
              </div>
            </div>
            <div className='flex flex-col sm:flex-row'>
              <div>
                <label htmlFor='dateFrom' className='block'>
                  {t('RangeLabelFrom')}
                </label>
                <Calendar
                  id='dateFrom'
                  value={fromDate?.toJSDate()}
                  maxDate={toDate?.toJSDate()}
                  onChange={(e) => {
                    if (e.value) {
                      setSelectedRange('Periodo specifico')
                      setFromDate(DateTime.fromJSDate(e.value))
                    } else {
                      setFromDate(undefined)
                    }
                  }}
                  showIcon
                  showButtonBar
                  placeholder={t('DateFormatPlaceholder')}
                />
              </div>
              <div className='pl-0 sm:pl-3'>
                <label htmlFor='dateTo' className='block'>
                  {t('RangeLabelTo')}
                </label>
                <Calendar
                  id='dateTo'
                  value={toDate?.toJSDate()}
                  minDate={fromDate?.toJSDate()}
                  maxDate={today.toJSDate()}
                  onChange={(e) => {
                    if (e.value) {
                      setSelectedRange('Periodo specifico')
                      setToDate(DateTime.fromJSDate(e.value).endOf('day'))
                    } else {
                      setToDate(undefined)
                    }
                  }}
                  showIcon
                  showButtonBar
                  placeholder={t('DateFormatPlaceholder')}
                />
              </div>
            </div>
            <div className='mt-4 text-right'>
              <Button
                label={t('FiltersClearLabelButton')}
                link
                icon='pi pi-trash'
                className='mr-4'
                onClick={() => {
                  setSelectedStatusToDisplay(undefined)
                  setSelectedStatusFilters(undefined)
                  setFromDate(undefined)
                  setToDate(undefined)
                  setSelectedRange(undefined)
                  props.updateFilters(props.defaultFilters)
                }}
              ></Button>
              <Button
                label={t('RangeLabelApplyButton')}
                rounded
                icon='pi pi-search'
                onClick={() => {
                  props.updateFilters({
                    ...props.selectedFilters,
                    status: selectedStatusFilters,
                    from: fromDate?.toISO()?.toString(),
                    to: toDate?.toISO()?.toString(),
                    offset: 0,
                  })
                }}
              ></Button>
            </div>
          </div>

          <div className='mb-4 hidden gap-2 xl:grid'>
            <div className='flex flex-row'>
              <div>
                <label htmlFor='filters' className='mr-1 block'>
                  {t('StatusFilterLabel')}
                </label>
                <MultiSelect
                  id='filters'
                  pt={{
                    header: { className: 'pt-1 pl-4' },
                    checkbox: { root: { className: 'flex vertical-middle' } },
                  }}
                  value={selectedStatusToDisplay}
                  onChange={(e) => {
                    if (e.value.length > 0) {
                      const filterArray: string[] = e.value.map((v: StatusConfigType) => v.key)
                      setSelectedStatusFilters(filterArray)
                    } else {
                      setSelectedStatusFilters(props.defaultFilters.status)
                    }
                    setSelectedStatusToDisplay(e.value)
                  }}
                  options={props.statusList}
                  optionLabel='label'
                  placeholder={t('StatusFilterDropdownPlaceholder')}
                  className='w-64'
                />
              </div>
            </div>
            <div>
              {t('RangeLabel')}
              <div className='flex flex-row'>
                <div>
                  {dateRanges.map((dateRangeObj, index) => (
                    <Button
                      key={index}
                      rounded
                      label={dateRangeObj.label}
                      onClick={() => handleDateRangeChange(dateRangeObj.range, dateRangeObj.label)}
                      className={selectedRange === dateRangeObj.label ? 'p-button-primary' : 'p-button-outlined'}
                      style={{ margin: '5px' }}
                    />
                  ))}
                </div>
                <div className='ml-4 p-1'>
                  <label className='mr-1' htmlFor='dateFrom'>
                    {t('RangeLabelFrom')}
                  </label>
                  <Calendar
                    id='dateFrom'
                    value={fromDate?.toJSDate()}
                    maxDate={toDate?.toJSDate()}
                    onChange={(e) => {
                      if (e.value) {
                        setSelectedRange('Periodo specifico')
                        setFromDate(DateTime.fromJSDate(e.value))
                      } else {
                        setFromDate(undefined)
                      }
                    }}
                    showIcon
                    showButtonBar
                    placeholder={t('DateFormatPlaceholder')}
                  />

                  <label className='ml-2 mr-1' htmlFor='dateTo'>
                    {t('RangeLabelTo')}
                  </label>
                  <Calendar
                    id='dateTo'
                    value={toDate?.toJSDate()}
                    minDate={fromDate?.toJSDate()}
                    maxDate={today.toJSDate()}
                    onChange={(e) => {
                      if (e.value) {
                        setSelectedRange('Periodo specifico')
                        setToDate(DateTime.fromJSDate(e.value).endOf('day'))
                      } else {
                        setToDate(undefined)
                      }
                    }}
                    showIcon
                    showButtonBar
                    placeholder={t('DateFormatPlaceholder')}
                  />
                </div>
              </div>
            </div>

            <div className='mt-4 text-right'>
              <Button
                label={t('FiltersClearLabelButton')}
                link
                icon='pi pi-trash'
                className='mr-4'
                onClick={() => {
                  setSelectedStatusToDisplay(undefined)
                  setSelectedStatusFilters(props.defaultFilters.status)
                  setFromDate(undefined)
                  setToDate(undefined)
                  setSelectedRange(undefined)
                  props.updateFilters(props.defaultFilters)
                }}
              ></Button>
              <Button
                label={t('RangeLabelApplyButton')}
                rounded
                icon='pi pi-search'
                onClick={() => {
                  console.log('apply filters with status: ', selectedStatusFilters)
                  props.updateFilters({
                    ...props.selectedFilters,
                    status: selectedStatusFilters,
                    from: fromDate?.toISO()?.toString(),
                    to: toDate?.toISO()?.toString(),
                    offset: 0,
                  })
                }}
              ></Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  )
}
